import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import type { LevelConfig, CheckpointConfig, StageConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

type GameState = 'idle' | 'running' | 'success' | 'failed';
type CurrentStage = 1 | 2 | 3 | 4;

const STAGE_COLORS: Record<CurrentStage, number> = {
  1: 0x4ade80,
  2: 0x3b82f6,
  3: 0xf59e0b,
  4: 0xef4444,
};

const CHECKPOINT_COLOR = 0xffa500;
const GOAL_COLOR = 0x4ade80;

export class Level5Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private levelConfig: LevelConfig | null = null;
  private gameState: GameState = 'idle';
  private currentStage: CurrentStage = 1;
  private checkpoints: CheckpointConfig[] = [];
  private stages: StageConfig[] = [];
  private completedStages: Set<number> = new Set();
  private checkpointGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private goalZone!: Phaser.Geom.Rectangle;
  private isVictory: boolean = false;
  private stageText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private elapsedTime: number = 0;

  constructor() {
    super({ key: 'Level5Scene' });
  }

  preload(): void {
    this.load.json('level-5', 'src/levels/levels/level-5.json');
  }

  create(): void {
    this.levelConfig = this.cache.json.get('level-5') as LevelConfig;

    if (this.levelConfig) {
      this.checkpoints = this.levelConfig.checkpoints ?? [];
      this.stages = this.levelConfig.stages ?? [];
    }

    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(
        this.levelConfig?.physics.gravity.x ?? 0,
        this.levelConfig?.physics.gravity.y ?? -10
      ),
      allowSleep: this.levelConfig?.physics.allowSleep ?? true,
    });

    this.createBackground();
    this.createBoundaries();
    this.createPlatforms();
    this.createCheckpoints();
    this.createGoalZone();
    this.createStickman();
    this.createUI();
    this.setupInput();

    this.gameState = 'running';
    this.currentStage = 1;
    this.completedStages = new Set();
    this.isVictory = false;
    this.elapsedTime = 0;
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running' || this.isVictory) return;

    const dt = delta / 1000;
    this.elapsedTime += dt;
    this.physicsWorld.step(dt);
    this.stickman.update();
    this.checkCheckpointCollisions();
    this.checkVictory();
    this.updateStageDisplay();
  }

  private createBackground(): void {
    this.cameras.main.setBackgroundColor('#1a1a2e');

    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2a2a4a, 0.3);
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, GAME_HEIGHT);
      grid.strokePath();
    }
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(GAME_WIDTH, y);
      grid.strokePath();
    }
  }

  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private createPlatforms(): void {
    const platforms = this.levelConfig?.platforms ?? [];
    const platformGraphics = this.add.graphics();

    for (const platform of platforms) {
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction: platform.friction ?? 0.5,
        restitution: platform.restitution ?? 0,
      });

      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      const color = platform.color ?? 0x4a4a6a;

      platformGraphics.fillStyle(color, 1);
      platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
    }
  }

  private createCheckpoints(): void {
    for (const checkpoint of this.checkpoints) {
      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const color = checkpoint.color ?? CHECKPOINT_COLOR;

      const graphics = this.add.graphics();
      graphics.fillStyle(color, 0.3);
      graphics.fillRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);
      graphics.lineStyle(3, color);
      graphics.strokeRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);

      this.checkpointGraphics.set(checkpoint.id, graphics);

      this.add.text(screenX + checkpoint.size.width / 2, screenY - 10, `阶段${checkpoint.stage}`, {
        fontSize: '12px',
        color: '#ffa500',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createGoalZone(): void {
    const goal = this.levelConfig?.goal;
    if (!goal) return;

    const screenX = goal.position.x - goal.size.width / 2;
    const screenY = GAME_HEIGHT - goal.position.y - goal.size.height / 2;

    this.goalZone = new Phaser.Geom.Rectangle(
      screenX,
      screenY,
      goal.size.width,
      goal.size.height
    );

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(GOAL_COLOR, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, GOAL_COLOR);
    goalGraphics.strokeRectShape(this.goalZone);

    this.add.text(this.goalZone.centerX, this.goalZone.y - 10, '终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    const startY = playerConfig?.startPosition.y ?? 100;
    const startX = playerConfig?.startPosition.x ?? 100;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: 8,
        jumpForce: 200,
      },
      GAME_HEIGHT
    );
  }

  private createUI(): void {
    this.add.text(GAME_WIDTH / 2, 30, '综合挑战 - Phase 1 最终关卡', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.stageText = this.add.text(GAME_WIDTH / 2, 60, '', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.hintText = this.add.text(GAME_WIDTH / 2, 85, '', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.progressText = this.add.text(10, 10, '进度: 阶段1/4', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.createStageIndicators();
    this.updateStageDisplay();
  }

  private createStageIndicators(): void {
    const startX = 10;
    const y = 560;
    const width = 60;
    const gap = 10;

    for (let i = 1; i <= 4; i++) {
      const x = startX + (i - 1) * (width + gap);
      const stageConfig = this.stages.find(s => s.stage === i);

      const bg = this.add.graphics();
      bg.fillStyle(STAGE_COLORS[i as CurrentStage], 0.3);
      bg.fillRect(x, y, width, 30);
      bg.lineStyle(2, STAGE_COLORS[i as CurrentStage]);
      bg.strokeRect(x, y, width, 30);

      this.add.text(x + width / 2, y + 15, stageConfig?.name ?? `阶段${i}`, {
        fontSize: '10px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private setupInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        this.resetLevel();
      }
    });
  }

  private checkCheckpointCollisions(): void {
    const headPos = this.stickman.getHeadPosition();

    for (const checkpoint of this.checkpoints) {
      if (this.completedStages.has(checkpoint.stage ?? 0)) continue;

      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const checkpointRect = new Phaser.Geom.Rectangle(
        screenX,
        screenY,
        checkpoint.size.width,
        checkpoint.size.height
      );

      if (checkpointRect.contains(headPos.x, headPos.y)) {
        this.completeStage(checkpoint.stage ?? 1);
      }
    }
  }

  private completeStage(stage: number): void {
    if (this.completedStages.has(stage)) return;

    this.completedStages.add(stage);

    const graphics = this.checkpointGraphics.get(`checkpoint-${stage}`);
    if (graphics) {
      graphics.clear();
      graphics.fillStyle(0x4ade80, 0.5);
      graphics.fillRect(
        graphics.x,
        graphics.y,
        this.checkpoints.find(c => c.stage === stage)?.size.width ?? 60,
        this.checkpoints.find(c => c.stage === stage)?.size.height ?? 80
      );
      graphics.strokeRect(
        graphics.x,
        graphics.y,
        this.checkpoints.find(c => c.stage === stage)?.size.width ?? 60,
        this.checkpoints.find(c => c.stage === stage)?.size.height ?? 80
      );
    }

    this.showStageTransition(stage);

    if (stage < 4) {
      this.currentStage = (stage + 1) as CurrentStage;
    }
  }

  private showStageTransition(stage: number): void {
    const stageConfig = this.stages.find(s => s.stage === stage);
    const transitionBg = this.add.graphics();
    transitionBg.fillStyle(0x000000, 0.7);
    transitionBg.fillRect(GAME_WIDTH / 2 - 150, GAME_HEIGHT / 2 - 60, 300, 120);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, `阶段${stage} 完成！`, {
      fontSize: '24px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    if (stageConfig) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, stageConfig.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    this.time.delayedCall(1500, () => {
      transitionBg.destroy();
    });
  }

  private updateStageDisplay(): void {
    const stageConfig = this.stages.find(s => s.stage === this.currentStage);
    if (stageConfig) {
      this.stageText.setText(`当前: ${stageConfig.name}`);
      this.stageText.setColor(`#${STAGE_COLORS[this.currentStage].toString(16).padStart(6, '0')}`);
      this.hintText.setText(stageConfig.hint ?? '');
    }
    this.progressText.setText(`进度: 阶段${this.currentStage}/4 (${this.completedStages.size}/4 完成)`);
  }

  private checkVictory(): void {
    const headPos = this.stickman.getHeadPosition();

    if (this.goalZone.contains(headPos.x, headPos.y) && this.completedStages.size >= 3) {
      this.isVictory = true;
      this.gameState = 'success';
      this.showVictoryMessage();
    }
  }

  private showVictoryMessage(): void {
    const saveGameState = getGameState();
    saveGameState.completeLevel(5, this.elapsedTime);

    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.8);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60, '🎉 综合挑战完成！', {
      fontSize: '36px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      '恭喜你完成了 Phase 1 的所有关卡！\n你已经掌握了速度、加速度、抛物线和摩擦力。',
      {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    const backBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 60, '返回', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 60, '重新挑战', {
      fontSize: '20px',
      color: '#ffd700',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetLevel();
    });
  }

  private resetLevel(): void {
    this.scene.restart();
  }

  getCurrentStage(): CurrentStage {
    return this.currentStage;
  }

  getCompletedStages(): Set<number> {
    return this.completedStages;
  }

  getIsVictory(): boolean {
    return this.isVictory;
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getGameState(): GameState {
    return this.gameState;
  }
}